import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Download,
  Users,
  Building2,
  ClipboardList,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  Loader,
  TrendingUp,
  Calendar,
  MapPin,
  DollarSign,
  PieChart as PieChartIcon,
  Activity,
  FileText,
  X,
  ChevronDown,
  FileSpreadsheet
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts';
import reportsService from '../services/reportsService';
import { useToast } from '../context/ToastContext';
import './ReportsPage.css';

const COLORS = ['#1a5f4a', '#c4a35a', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const propertyTypeLabels = {
  villa: 'فيلا',
  apartment: 'شقة',
  land: 'أرض',
  building: 'عمارة',
  duplex: 'دوبلكس',
  commercial: 'تجاري',
  farm: 'مزرعة',
  rest_house: 'استراحة'
};

const statusLabels = {
  new: 'جديد',
  contacted: 'تم التواصل',
  closed: 'مغلق',
  active: 'نشط',
  sold: 'مباع',
  pending: 'معلق',
  expired: 'منتهي',
  cancelled: 'ملغي'
};

const ReportsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('month');
  const [loading, setLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const exportDropdownRef = useRef(null);
  const [data, setData] = useState({
    overview: null,
    users: null,
    offers: null,
    requests: null,
    subscriptions: null
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = useCallback(async (tab) => {
    try {
      setLoading(true);
      let response;
      
      switch (tab) {
        case 'users':
          response = await reportsService.getUsersReport({ dateRange });
          break;
        case 'offers':
          response = await reportsService.getOffersReport({ dateRange });
          break;
        case 'requests':
          response = await reportsService.getRequestsReport({ dateRange });
          break;
        case 'subscriptions':
          response = await reportsService.getSubscriptionsReport({ dateRange });
          break;
        default:
          response = await reportsService.getReports(dateRange);
      }
      
      if (response.success) {
        setData(prev => ({ ...prev, [tab]: response.data }));
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab, dateRange, fetchData]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const formatPrice = (price) => {
    if (!price) return '0 ر.س';
    return `${price.toLocaleString()} ر.س`;
  };

  const tabs = [
    { id: 'overview', label: 'نظرة عامة', icon: BarChart3 },
    { id: 'users', label: 'المستخدمين', icon: Users },
    { id: 'offers', label: 'العروض', icon: Building2 },
    { id: 'requests', label: 'الطلبات', icon: ClipboardList },
    { id: 'subscriptions', label: 'الاشتراكات', icon: CreditCard }
  ];

  const exportOptions = [
    { id: 'overview', label: 'نظرة عامة', icon: BarChart3 },
    { id: 'users', label: 'تقرير المستخدمين', icon: Users },
    { id: 'offers', label: 'تقرير العروض', icon: Building2 },
    { id: 'requests', label: 'تقرير الطلبات', icon: ClipboardList },
    { id: 'subscriptions', label: 'تقرير الاشتراكات', icon: CreditCard }
  ];

  const handleExportClick = async (type) => {
    setShowExportDropdown(false);
    if (type) {
      // Export directly using server endpoint with current date range
      try {
        const now = new Date();
        let startDate, endDate = now.toISOString().split('T')[0];
        
        switch (dateRange) {
          case 'week':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            break;
          case 'quarter':
            startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0];
            break;
          case 'year':
            startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
            break;
          default: // month
            startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        }
        
        await handleServerExport(type, { startDate, endDate });
        toast.success('تم تصدير التقرير بنجاح');
      } catch (error) {
        toast.error('فشل تصدير التقرير');
        console.error('Export error:', error);
      }
    } else {
      // Show modal for custom date range
      setShowExportModal(true);
    }
  };

  const handleServerExport = async (type, dateRange = {}) => {
    try {
      await reportsService.exportReport(type, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      return { success: true };
    } catch (error) {
      console.error('Server export error:', error);
      throw error;
    }
  };

  const handleExportWithDates = async (type) => {
    try {
      await handleServerExport(type, exportDateRange);
      setShowExportModal(false);
      toast.success('تم تصدير التقرير بنجاح');
    } catch (error) {
      toast.error('فشل تصدير التقرير');
      console.error('Export error:', error);
    }
  };

  const renderOverview = () => {
    const overview = data.overview;
    if (!overview) return null;

    const iconMap = { Users, Building2, ClipboardList, Eye: Activity };

    return (
      <>
        {/* Summary Stats */}
        <div className="stats-grid">
          {(overview.summaryStats || []).map((stat, index) => {
            const Icon = iconMap[stat.iconType] || Activity;
            const isPositive = stat.positive !== false;
            return (
              <div key={index} className="stat-card">
                <div className="stat-icon" style={{ background: `${stat.color}15` }}>
                  <Icon size={24} style={{ color: stat.color }} />
                </div>
                <div className="stat-content">
                  <span className="stat-label">{stat.label}</span>
                  <span className="stat-value">{stat.value}</span>
                  <span className={`stat-change ${isPositive ? 'positive' : 'negative'}`}>
                    {isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                    {stat.change}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Grid */}
        <div className="charts-grid">
          {/* Growth Chart */}
          <div className="chart-card full-width">
            <div className="chart-header">
              <h3><TrendingUp size={20} /> اتجاهات النمو</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={overview.monthlyData || []}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1a5f4a" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#1a5f4a" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorOffers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#c4a35a" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#c4a35a" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#555" 
                    fontSize={13} 
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickLine={{ stroke: '#999' }}
                  />
                  <YAxis 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickLine={{ stroke: '#999' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #1a5f4a', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    labelStyle={{ fontWeight: '700', marginBottom: '8px', color: '#1a5f4a' }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }}
                  />
                  <Area type="monotone" dataKey="users" stroke="#1a5f4a" strokeWidth={2} fill="url(#colorUsers)" name="المستخدمين" />
                  <Area type="monotone" dataKey="offers" stroke="#c4a35a" strokeWidth={2} fill="url(#colorOffers)" name="العروض" />
                  <Area type="monotone" dataKey="requests" stroke="#3b82f6" strokeWidth={2} fill="url(#colorRequests)" name="الطلبات" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Property Types */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><PieChartIcon size={20} /> أنواع العقارات</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={overview.propertyTypeData || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {(overview.propertyTypeData || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, propertyTypeLabels[name] || name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pie-legend">
                {(overview.propertyTypeData || []).map((item, index) => (
                  <div key={index} className="legend-item">
                    <span className="legend-color" style={{ background: item.color || COLORS[index % COLORS.length] }}></span>
                    <span className="legend-label">{propertyTypeLabels[item.name] || item.name}</span>
                    <span className="legend-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cities */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><MapPin size={20} /> أفضل المدن</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={overview.cityData || []} layout="vertical" barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="city" 
                    stroke="#555" 
                    fontSize={14}
                    fontWeight={700}
                    width={100}
                    tick={{ fill: '#1a5f4a' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #1a5f4a', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    cursor={{ fill: 'rgba(26, 95, 74, 0.1)' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                  <Bar dataKey="offers" fill="#1a5f4a" name="العروض" radius={[0, 6, 6, 0]} barSize={20}>
                    {(overview.cityData || []).map((entry, index) => (
                      <Cell key={`offers-${index}`} fill="#1a5f4a" />
                    ))}
                  </Bar>
                  <Bar dataKey="requests" fill="#c4a35a" name="الطلبات" radius={[0, 6, 6, 0]} barSize={20}>
                    {(overview.cityData || []).map((entry, index) => (
                      <Cell key={`requests-${index}`} fill="#c4a35a" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderUsersReport = () => {
    const users = data.users;
    if (!users) return null;

    return (
      <>
        {/* Summary */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#1a5f4a15' }}>
              <Users size={24} style={{ color: '#1a5f4a' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">إجمالي المستخدمين</span>
              <span className="stat-value">{formatNumber(users.summary?.totalUsers)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#10b98115' }}>
              <Activity size={24} style={{ color: '#10b981' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">المستخدمين النشطين</span>
              <span className="stat-value">{formatNumber(users.summary?.activeUsers)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#3b82f615' }}>
              <TrendingUp size={24} style={{ color: '#3b82f6' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">مستخدمين جدد</span>
              <span className="stat-value">{formatNumber(users.summary?.newUsersInPeriod)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#c4a35a15' }}>
              <CreditCard size={24} style={{ color: '#c4a35a' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">مشتركين نشطين</span>
              <span className="stat-value">{formatNumber(users.summary?.usersWithActiveSubscription)}</span>
            </div>
          </div>
        </div>

        <div className="charts-grid">
          {/* Daily Trend */}
          <div className="chart-card full-width">
            <div className="chart-header">
              <h3><Calendar size={20} /> التسجيلات اليومية</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={users.dailyTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#555" 
                    fontSize={12}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #1a5f4a', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    labelStyle={{ fontWeight: '700', marginBottom: '8px', color: '#1a5f4a' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#1a5f4a" 
                    strokeWidth={3} 
                    dot={{ fill: '#1a5f4a', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 8, fill: '#1a5f4a', stroke: '#fff', strokeWidth: 2 }}
                    name="التسجيلات" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Users by City */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><MapPin size={20} /> المستخدمين حسب المدينة</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={users.usersByCity || []} layout="vertical" barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="city" 
                    stroke="#555" 
                    fontSize={14}
                    fontWeight={700}
                    width={100}
                    tick={{ fill: '#1a5f4a' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #1a5f4a', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    cursor={{ fill: 'rgba(26, 95, 74, 0.1)' }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#1a5f4a" 
                    name="المستخدمين" 
                    radius={[0, 6, 6, 0]}
                    barSize={24}
                    label={{ position: 'right', fill: '#1a5f4a', fontWeight: 700, fontSize: 13 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Users Tables */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><Building2 size={20} /> أكثر المستخدمين نشاطاً (عروض)</h3>
            </div>
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الاسم</th>
                    <th>المدينة</th>
                    <th>العروض</th>
                  </tr>
                </thead>
                <tbody>
                  {(users.topUsersByOffers || []).slice(0, 5).map((user, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{user.name || 'غير محدد'}</td>
                      <td>{user.city || '-'}</td>
                      <td><span className="badge">{user.offersCount}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderOffersReport = () => {
    const offers = data.offers;
    if (!offers) return null;

    return (
      <>
        {/* Summary */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#1a5f4a15' }}>
              <Building2 size={24} style={{ color: '#1a5f4a' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">إجمالي العروض</span>
              <span className="stat-value">{formatNumber(offers.summary?.totalOffers)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#10b98115' }}>
              <Activity size={24} style={{ color: '#10b981' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">العروض النشطة</span>
              <span className="stat-value">{formatNumber(offers.summary?.activeOffers)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#c4a35a15' }}>
              <DollarSign size={24} style={{ color: '#c4a35a' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">تم البيع</span>
              <span className="stat-value">{formatNumber(offers.summary?.soldOffers)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#3b82f615' }}>
              <TrendingUp size={24} style={{ color: '#3b82f6' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">عروض جديدة</span>
              <span className="stat-value">{formatNumber(offers.summary?.newOffersInPeriod)}</span>
            </div>
          </div>
        </div>

        <div className="charts-grid">
          {/* Daily Trend */}
          <div className="chart-card full-width">
            <div className="chart-header">
              <h3><Calendar size={20} /> العروض اليومية</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={offers.dailyTrend || []}>
                  <defs>
                    <linearGradient id="colorOffersTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#c4a35a" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#c4a35a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#555" 
                    fontSize={12}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #c4a35a', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    labelStyle={{ fontWeight: '700', marginBottom: '8px', color: '#c4a35a' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#c4a35a" 
                    strokeWidth={3} 
                    fill="url(#colorOffersTrend)" 
                    name="العروض"
                    dot={{ fill: '#c4a35a', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 7, fill: '#c4a35a', stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Offers by Type */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><PieChartIcon size={20} /> حسب نوع العقار</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={(offers.offersByType || []).map(item => ({
                      ...item,
                      name: propertyTypeLabels[item.type] || item.type
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#555', strokeWidth: 1 }}
                  >
                    {(offers.offersByType || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #1a5f4a', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    formatter={(value, name) => [`${value} عرض`, name]}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px', fontSize: '13px', fontWeight: '600' }}
                    formatter={(value) => <span style={{ color: '#333', fontWeight: 600 }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Price Distribution */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><DollarSign size={20} /> توزيع الأسعار</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={offers.priceDistribution || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" vertical={false} />
                  <XAxis 
                    dataKey="label" 
                    stroke="#555" 
                    fontSize={11}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    interval={0}
                  />
                  <YAxis 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #1a5f4a', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    formatter={(value) => [`${value} عرض`, 'العدد']}
                    cursor={{ fill: 'rgba(26, 95, 74, 0.1)' }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#1a5f4a" 
                    name="العروض" 
                    radius={[6, 6, 0, 0]}
                    label={{ position: 'top', fill: '#1a5f4a', fontWeight: 700, fontSize: 12 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Average Price by Type */}
          <div className="chart-card full-width">
            <div className="chart-header">
              <h3><DollarSign size={20} /> متوسط الأسعار حسب النوع</h3>
            </div>
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>نوع العقار</th>
                    <th>متوسط السعر</th>
                    <th>أقل سعر</th>
                    <th>أعلى سعر</th>
                  </tr>
                </thead>
                <tbody>
                  {(offers.avgPriceByType || []).map((item, index) => (
                    <tr key={index}>
                      <td>{propertyTypeLabels[item.type] || item.type}</td>
                      <td>{formatPrice(item.avgPrice)}</td>
                      <td>{formatPrice(item.minPrice)}</td>
                      <td>{formatPrice(item.maxPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderRequestsReport = () => {
    const requests = data.requests;
    if (!requests) return null;

    return (
      <>
        {/* Summary */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#3b82f615' }}>
              <ClipboardList size={24} style={{ color: '#3b82f6' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">إجمالي الطلبات</span>
              <span className="stat-value">{formatNumber(requests.summary?.totalRequests)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#10b98115' }}>
              <FileText size={24} style={{ color: '#10b981' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">طلبات جديدة</span>
              <span className="stat-value">{formatNumber(requests.summary?.newRequests)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#c4a35a15' }}>
              <Activity size={24} style={{ color: '#c4a35a' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">من النموذج العام</span>
              <span className="stat-value">{formatNumber(requests.summary?.publicFormRequests)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#1a5f4a15' }}>
              <TrendingUp size={24} style={{ color: '#1a5f4a' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">نسبة التحويل</span>
              <span className="stat-value">{requests.summary?.conversionRate}</span>
            </div>
          </div>
        </div>

        <div className="charts-grid">
          {/* Daily Trend */}
          <div className="chart-card full-width">
            <div className="chart-header">
              <h3><Calendar size={20} /> الطلبات اليومية</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={requests.dailyTrend || []}>
                  <defs>
                    <linearGradient id="colorRequestsTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#555" 
                    fontSize={12}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #3b82f6', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    labelStyle={{ fontWeight: '700', marginBottom: '8px', color: '#3b82f6' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#3b82f6" 
                    strokeWidth={3} 
                    fill="url(#colorRequestsTrend)" 
                    name="الطلبات"
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 7, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Requests by Status */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><PieChartIcon size={20} /> حسب الحالة</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={(requests.requestsByStatus || []).map(item => ({
                      ...item,
                      name: statusLabels[item.status] || item.status
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#555', strokeWidth: 1 }}
                  >
                    {(requests.requestsByStatus || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #3b82f6', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    formatter={(value, name) => [`${value} طلب`, name]}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px', fontSize: '13px', fontWeight: '600' }}
                    formatter={(value) => <span style={{ color: '#333', fontWeight: 600 }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Requests by Source */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><Activity size={20} /> حسب المصدر</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={(requests.requestsBySource || []).map(item => ({
                      ...item,
                      name: item.source === 'public_form' ? 'النموذج العام' : 'المستخدم'
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#555', strokeWidth: 1 }}
                  >
                    {(requests.requestsBySource || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index === 0 ? '#1a5f4a' : '#c4a35a'} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #3b82f6', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    formatter={(value, name) => [`${value} طلب`, name]}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px', fontSize: '13px', fontWeight: '600' }}
                    formatter={(value) => <span style={{ color: '#333', fontWeight: 600 }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Budget Distribution */}
          <div className="chart-card full-width">
            <div className="chart-header">
              <h3><DollarSign size={20} /> توزيع الميزانيات</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={requests.budgetDistribution || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" vertical={false} />
                  <XAxis 
                    dataKey="label" 
                    stroke="#555" 
                    fontSize={11}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    interval={0}
                  />
                  <YAxis 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #3b82f6', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    formatter={(value) => [`${value} طلب`, 'العدد']}
                    cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#3b82f6" 
                    name="الطلبات" 
                    radius={[6, 6, 0, 0]}
                    label={{ position: 'top', fill: '#3b82f6', fontWeight: 700, fontSize: 12 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderSubscriptionsReport = () => {
    const subs = data.subscriptions;
    if (!subs) return null;

    return (
      <>
        {/* Summary */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#1a5f4a15' }}>
              <CreditCard size={24} style={{ color: '#1a5f4a' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">الاشتراكات النشطة</span>
              <span className="stat-value">{formatNumber(subs.summary?.activeSubscriptions)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#10b98115' }}>
              <DollarSign size={24} style={{ color: '#10b981' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">إجمالي الإيرادات</span>
              <span className="stat-value">{formatPrice(subs.summary?.totalRevenue)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#3b82f615' }}>
              <TrendingUp size={24} style={{ color: '#3b82f6' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">إيرادات الفترة</span>
              <span className="stat-value">{formatPrice(subs.summary?.revenueInPeriod)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f59e0b15' }}>
              <Calendar size={24} style={{ color: '#f59e0b' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">تنتهي قريباً</span>
              <span className="stat-value">{formatNumber(subs.summary?.expiringSoon)}</span>
            </div>
          </div>
        </div>

        <div className="charts-grid">
          {/* Monthly Revenue */}
          <div className="chart-card full-width">
            <div className="chart-header">
              <h3><DollarSign size={20} /> الإيرادات الشهرية</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={subs.monthlyRevenue || []}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                  />
                  <YAxis 
                    stroke="#555" 
                    fontSize={13}
                    fontWeight={600}
                    tick={{ fill: '#333' }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #10b981', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    formatter={(value) => [formatPrice(value), 'الإيرادات']}
                    labelStyle={{ fontWeight: '700', marginBottom: '8px', color: '#10b981' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#10b981" 
                    strokeWidth={3} 
                    fill="url(#colorRevenue)" 
                    name="الإيرادات"
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 7, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Subscriptions by Plan */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><PieChartIcon size={20} /> حسب الخطة</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={subs.subscriptionsByPlan || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="total"
                    nameKey="plan"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#555', strokeWidth: 1 }}
                  >
                    {(subs.subscriptionsByPlan || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '2px solid #10b981', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                    formatter={(value, name) => [`${value} اشتراك`, name]}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px', fontSize: '13px', fontWeight: '600' }}
                    formatter={(value) => <span style={{ color: '#333', fontWeight: 600 }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Plans Table */}
          <div className="chart-card">
            <div className="chart-header">
              <h3><CreditCard size={20} /> الخطط المتاحة</h3>
            </div>
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>الخطة</th>
                    <th>السعر</th>
                    <th>المدة</th>
                  </tr>
                </thead>
                <tbody>
                  {(subs.plans || []).map((plan, index) => (
                    <tr key={index}>
                      <td>{plan.name}</td>
                      <td>{formatPrice(plan.price)}</td>
                      <td>{plan.duration} يوم</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <Loader className="spin" size={40} />
        </div>
      );
    }

    switch (activeTab) {
      case 'users':
        return renderUsersReport();
      case 'offers':
        return renderOffersReport();
      case 'requests':
        return renderRequestsReport();
      case 'subscriptions':
        return renderSubscriptionsReport();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <div className="header-title">
          <BarChart3 size={28} />
          <h1>{t('reports.title')}</h1>
        </div>
        <div className="header-actions">
          <select
            className="filter-select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="week">هذا الأسبوع</option>
            <option value="month">هذا الشهر</option>
            <option value="quarter">هذا الربع</option>
            <option value="year">هذا العام</option>
          </select>
          
          {/* Export Dropdown */}
          <div className="export-dropdown-container" ref={exportDropdownRef}>
            <button 
              className="btn btn-primary"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              <Download size={18} />
              تصدير التقرير
              <ChevronDown size={16} />
            </button>
            
            {showExportDropdown && (
              <div className="export-dropdown">
                <div className="export-dropdown-header">
                  <span>اختر نوع التقرير</span>
                </div>
                {exportOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.id}
                      className="export-dropdown-item"
                      onClick={() => handleExportClick(option.id)}
                    >
                      <Icon size={18} />
                      <span>{option.label}</span>
                      <FileSpreadsheet size={16} className="file-icon" />
                    </button>
                  );
                })}
                <div className="export-dropdown-divider"></div>
                <button
                  className="export-dropdown-item custom-range"
                  onClick={() => handleExportClick(null)}
                >
                  <Calendar size={18} />
                  <span>تحديد نطاق تاريخ مخصص</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Download size={22} />
                تصدير التقارير
              </h3>
              <button className="close-btn" onClick={() => setShowExportModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="date-range-section">
                <h4>
                  <Calendar size={18} />
                  حدد نطاق التاريخ
                </h4>
                <div className="date-inputs">
                  <div className="date-input-group">
                    <label>من تاريخ</label>
                    <input
                      type="date"
                      value={exportDateRange.startDate}
                      onChange={(e) => setExportDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="date-input-group">
                    <label>إلى تاريخ</label>
                    <input
                      type="date"
                      value={exportDateRange.endDate}
                      onChange={(e) => setExportDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="quick-ranges">
                  <button onClick={() => {
                    const now = new Date();
                    setExportDateRange({
                      startDate: new Date(now.setDate(now.getDate() - 7)).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0]
                    });
                  }}>آخر أسبوع</button>
                  <button onClick={() => {
                    const now = new Date();
                    setExportDateRange({
                      startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0]
                    });
                  }}>هذا الشهر</button>
                  <button onClick={() => {
                    const now = new Date();
                    setExportDateRange({
                      startDate: new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0]
                    });
                  }}>آخر 3 أشهر</button>
                  <button onClick={() => {
                    const now = new Date();
                    setExportDateRange({
                      startDate: new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0]
                    });
                  }}>هذا العام</button>
                </div>
              </div>
              
              <div className="export-types-section">
                <h4>
                  <FileSpreadsheet size={18} />
                  اختر التقرير للتصدير
                </h4>
                <div className="export-types-grid">
                  {exportOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        className="export-type-card"
                        onClick={() => handleExportWithDates(option.id)}
                      >
                        <div className="export-type-icon">
                          <Icon size={24} />
                        </div>
                        <span>{option.label}</span>
                        <Download size={16} className="download-icon" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="reports-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
};

export default ReportsPage;
